import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import {
  Alert,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  Search as SearchIcon,
  ReplayCircleFilled as ReplayCircleFilledIcon,
} from '@mui/icons-material';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';

import ConfirmationDialog from '@components/ConfirmationDialog';
import MultiSelect from '@components/MultiSelect';
import { Typography } from '@components/styledComponents';
import { showErrorMessage, showSuccessMessage } from '@store/dashboard';

import { breadcrumbsActions } from '@store/breadcrumbs';
import { viewLPConfig } from '../conversion-rates.breadcrumbs';

import {
  getLoyaltyPointsConfig,
  loyaltyActions,
  selectIsFetchingLPConfig,
  selectIsUpdatingLPConfig,
  selectLoyaltyPointsConfig,
  updateLPConfig,
} from '../store/loyalty-points.slice';
import {
  getLOBs,
  selectIsFetchingLOBList,
  selectLOBList,
} from '../../payment-options/store/payment-options-merchants';

import ApplicationPageTemplate from '../../dashboard/components/ApplicationPageTemplate/ApplicationPageTemplate';
import { LP_LEFT_COLUMN_STYLE } from '../conversion-rates.constants';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

function SearchForm({
  handleSubmit,
  handleLOBChange,
  formErrorMsg,
  invalid,
  isFetchingCurrencies,
  isFetchingLOBs,
  isFetchingConfig,
  lobs,
  setFormErrorMsg,
  values,
}) {
  return (
    <form
      onSubmit={handleSubmit}
      noValidate>
      <Grid
        container
        spacing={2}
        alignItems="center">
        <Grid
          item
          xs={12}
          lg={4}>
          <MultiSelect
            id="lob"
            name="lob"
            label="Select LOB"
            required
            items={lobs}
            size="small"
            variant="outlined"
            readOnly={isFetchingLOBs}
            value={values.lob}
            isOptionEqualToValue={(option, value) =>
              option.lobId === value.lobId
            }
            multiple={false}
            optionDisplayKey="lobCode"
            onChange={handleLOBChange}
          />
        </Grid>
        <Grid
          item
          xs={12}
          lg={2}>
          <LoadingButton
            fullWidth
            sx={{ minHeight: '40px' }}
            variant="contained"
            type="submit"
            disabled={invalid}
            loading={isFetchingCurrencies || isFetchingConfig}
            startIcon={<SearchIcon />}
            loadingPosition="start">
            Search
          </LoadingButton>
        </Grid>
        {formErrorMsg && (
          <Grid
            item
            xs={12}>
            <Alert
              onClose={() => {
                setFormErrorMsg('');
              }}
              severity="error">
              {formErrorMsg}
            </Alert>
          </Grid>
        )}
      </Grid>
    </form>
  );
}

function LeftColumnHeader({ children }) {
  return (
    <TableCell
      component="th"
      scope="row"
      style={LP_LEFT_COLUMN_STYLE}>
      {children}
    </TableCell>
  );
}

function Actions({ permissions, updateLPConfigHandler }) {
  const isUpdatingLPConfig = useSelector(selectIsUpdatingLPConfig);
  const [showConfirmUpdateDialog, setShowConfirmUpdateDialog] = useState({
    show: false,
  });
  const { canUpdateLPConfig } = permissions;

  if (!canUpdateLPConfig) return null;

  return (
    <>
      <Typography
        className="form-header"
        variant="formHeader">
        Actions
      </Typography>
      <Box>
        <LoadingButton
          fullWidth={false}
          loading={isUpdatingLPConfig}
          variant="contained"
          onClick={() => {
            setShowConfirmUpdateDialog({ show: true });
          }}
          startIcon={<ReplayCircleFilledIcon />}
          style={{ marginRight: 10 }}>
          Update Config
        </LoadingButton>
      </Box>
      <ConfirmationDialog
        openDialog={showConfirmUpdateDialog}
        yesHandler={() => updateLPConfigHandler()}
        title="Update Loyalty Config"
        message="Are you sure you want to update the config?"
      />
    </>
  );
}

export default function LoyaltyPointsConfiguration({ permissions = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const formValues = useRef({ lob: null });

  const [formErrorMsg, setFormErrorMsg] = useState('');

  const isFetchingConfig = useSelector(selectIsFetchingLPConfig);
  const isFetchingLOBs = useSelector(selectIsFetchingLOBList);
  const lpConfig = useSelector(selectLoyaltyPointsConfig);
  const lobs = useSelector(selectLOBList);

  useEffect(() => {
    if (!permissions?.canViewLPConfig) {
      navigate('/landing');
    }
  }, [navigate, permissions]);

  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(viewLPConfig));
    dispatch(loyaltyActions.resetLPConfig());
    dispatch(getLOBs());
  }, [dispatch]);

  const fetchLoyaltyPointsConfig = (params) => {
    setFormErrorMsg('');

    if (!params) return;

    dispatch(getLoyaltyPointsConfig(params))
      .then(({ payload, error }) => {
        if (Object.keys(payload).length > 0) return;

        if (error?.message) {
          setFormErrorMsg(JSON.stringify(error.message));
          return;
        }

        if (payload?.message) {
          setFormErrorMsg(JSON.stringify(payload.message));
          return;
        }

        setFormErrorMsg('No config found');
      })
      .catch((error) => {
        setFormErrorMsg(error);
        return { [FORM_ERROR]: error };
      });
  };

  const onSubmit = (values) => {
    const { lob } = values;

    if (!lob) return;

    const payload = { lobName: lob.lobCode };

    fetchLoyaltyPointsConfig(payload);
  };

  const validate = (values) => {
    if (!values.lob) return { [FORM_ERROR]: 'LOB is required' };
    return {};
  };

  const handleLOBChange = useCallback((value) => {
    formValues.current.lob = value;
  }, []);

  const updateLPConfigHandler = () => {
    dispatch(updateLPConfig())
      .then(({ payload }) => {
        if (payload && payload.success) {
          dispatch(showSuccessMessage('Loyalty config updated successfully.'));
        } else {
          dispatch(
            showErrorMessage(
              payload.message
                ? payload.message
                : 'Failed to update loyalty config',
            ),
          );
        }
      })
      .catch((error) => {
        dispatch(showErrorMessage(error || 'Failed to update loyalty config'));
      });
  };

  const searchFormProps = {
    lobs,
    handleLOBChange,
    isFetchingLOBs,
    isFetchingConfig,
    formErrorMsg,
    setFormErrorMsg,
  };

  return (
    <ApplicationPageTemplate
      pageReady
      title="Loyalty Configuration">
      <Form
        onSubmit={onSubmit}
        initialValues={formValues.current}
        validate={validate}
        mutators={{
          setValue: ([field, value], state, { changeValue }) => {
            changeValue(state, field, () => value);
          },
        }}
        render={({
          handleSubmit,
          values,
          invalid,
          pristine,
          form: {
            mutators: { setValue },
          },
        }) => (
          <SearchForm
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...searchFormProps}
            handleSubmit={handleSubmit}
            pristine={pristine}
            invalid={invalid}
            setValue={setValue}
            values={values}
          />
        )}
      />
      {lpConfig && (
        <TableContainer sx={{ marginTop: '30px', maxWidth: '500px' }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <LeftColumnHeader>LOB</LeftColumnHeader>
                <TableCell>
                  {formValues.current.lob ? formValues.current.lob.lobCode : ''}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Table
            sx={{ marginTop: '40px' }}
            stickyHeader
            size="small">
            <TableHead>
              <TableRow>
                <LeftColumnHeader>Key</LeftColumnHeader>
                <TableCell>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(lpConfig).map(([key, value]) => (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={key}>
                  <LeftColumnHeader>{key}</LeftColumnHeader>
                  <TableCell>{JSON.stringify(value)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Actions
        permissions={permissions}
        updateLPConfigHandler={updateLPConfigHandler}
      />
    </ApplicationPageTemplate>
  );
}
